import Axios from 'axios';
import moment from 'moment';
import * as EntryAndExitRequest from '@/models/entryAndExitRequest';
import * as EntryAndExitResponse from '@/models/entryAndExitResponse';
import { EntryAndExitTypes } from '@/enums/entryAndExitTypes';

export type EnterLeave = EntryAndExitResponse.EntryAndExitDetailResponse;

export interface SearchCondition {
  dateFrom: Date | null;
  timeFrom: Date | null;
  dateTo: Date | null;
  timeTo: Date | null;
  entryAndExitType: EntryAndExitTypes;
  tagIdList: number[];
  keyWord: string;
  perPage: number;
  page: number;
  sort: string;
}

interface State {
  loading: boolean;
  countAll: number;
  enterLeaves: EnterLeave[];
  searchCondition: SearchCondition;
}
const mapState: State = {
  loading: false,
  countAll: 0,
  enterLeaves: new Array<EnterLeave>(),
  searchCondition: {
    dateFrom: null,
    timeFrom: null,
    dateTo: null,
    timeTo: null,
    entryAndExitType: EntryAndExitTypes.None,
    tagIdList: new Array<number>(),
    keyWord: '',
    perPage: 20,
    page: 1,
    sort: 'date descending',
  },
};

const mapActions = {
  async searchEnterLeaves({ commit }: any, searchCondition: SearchCondition) {
    commit('setLoading', true);
    const request = getRequestData(searchCondition);
    const response = await Axios.post('/api/EntryAndExit/Search', request).catch((error) => {
      commit('setLoading', false);
      throw error;
    });
    response.data.entryAndExitList.forEach((enterLeave: any) => {
      if (typeof enterLeave.date === 'string') {
        enterLeave.date = new Date(enterLeave.date + 'Z');
      }
    });
    commit('searchEnterLeaves', response.data);
  },
  async createEnterLeave(
    { commit }: any,
    { enterLeave, studentIdList }: { enterLeave: EnterLeave, studentIdList: string[] }
  ) {
    const request: EntryAndExitRequest.EntryAndExitRequest = {
      entryAndExitId: undefined,
      studentIdList,
      dateFrom: enterLeave.date,
      dateTo: undefined,
      entryAndExit: enterLeave.entryAndExit,
      tagIdList: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/EntryAndExit/Create', request).catch((error) => {
      throw new Error('入退室履歴の登録に失敗しました。');
    });
    return response.data;
  },
  async editEnterLeave({ commit }: any, enterLeave: EnterLeave) {
    const request: EntryAndExitRequest.EntryAndExitRequest = {
      entryAndExitId: enterLeave.id,
      studentIdList: undefined,
      dateFrom: enterLeave.date,
      dateTo: undefined,
      entryAndExit: enterLeave.entryAndExit,
      tagIdList: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/EntryAndExit/Edit', request).catch((error) => {
      throw new Error('入退室履歴の変更に失敗しました。');
    });
    commit('changeEnterLeave', enterLeave);
    return response.data;
  },
  async deleteEnterLeave({ commit }: any, id: string) {
    const request: EntryAndExitRequest.EntryAndExitRequest = {
      entryAndExitId: id,
      studentIdList: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      entryAndExit: undefined,
      tagIdList: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/EntryAndExit/Delete', request).catch((error) => {
      throw new Error('入退室履歴の削除に失敗しました。');
    });
    commit('removeEnterLeave', id);
    return response.data;
  },
  async getEnterLeave({ commit }: any, id: string) {
    const request: EntryAndExitRequest.EntryAndExitRequest = {
      entryAndExitId: id,
      studentIdList: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      entryAndExit: undefined,
      tagIdList: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
    };
    const response = await Axios.post('/api/EntryAndExit/Detail', request).catch((error) => {
      throw error;
    });
    if (typeof response.data.date === 'string') {
      response.data.date = new Date(response.data.date + 'Z');
    }
    return response.data as EnterLeave;
  },
  async downloadEnterLeaves({ commit }: any, searchCondition: SearchCondition) {
    const request = getRequestData(searchCondition);
    const response = await Axios.post(
      '/api/EntryAndExit/DownLoad',
      request,
      { responseType: 'blob' }
    ).catch((error) => {
      throw new Error('入退室履歴の取得に失敗しました。');
    });

    let fileName = '';
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      if (matches != null && matches[1]) {
        fileName = decodeURI(matches[1].replace(/['"]/g, ''));
      }
    }
    if (fileName === '') {
      fileName = '入退室履歴.xlsx';
    }

    if (!(window.navigator as any).msSaveOrOpenBlob) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const url = (window.navigator as any).msSaveOrOpenBlob(new Blob([response.data]), fileName);
    }
  },
};

function getRequestData(searchCondition: SearchCondition) {
  let dateTimeFrom: Date | undefined;
  if (!!searchCondition.dateFrom) {
    dateTimeFrom = searchCondition.dateFrom;
    if (!!searchCondition.timeFrom) {
      dateTimeFrom.setHours(searchCondition.timeFrom.getHours());
      dateTimeFrom.setMinutes(searchCondition.timeFrom.getMinutes());
    } else {
      dateTimeFrom.setHours(0);
      dateTimeFrom.setMinutes(0);
    }
  }
  let dateTimeTo: Date | undefined;
  if (!!searchCondition.dateTo) {
    dateTimeTo = searchCondition.dateTo;
    if (!!searchCondition.timeTo) {
      dateTimeTo.setHours(searchCondition.timeTo.getHours());
      dateTimeTo.setMinutes(searchCondition.timeTo.getMinutes());
    } else {
      dateTimeTo.setHours(23);
      dateTimeTo.setMinutes(59);
    }
  }
  const request: EntryAndExitRequest.EntryAndExitRequest = {
    entryAndExitId: undefined,
    studentIdList: undefined,
    dateFrom: dateTimeFrom,
    dateTo: dateTimeTo,
    entryAndExit: searchCondition.entryAndExitType,
    tagIdList: searchCondition.tagIdList,
    keyWord: searchCondition.keyWord,
    perPage: searchCondition.perPage,
    page: searchCondition.page,
    sort: searchCondition.sort,
  };
  return request;
}

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  searchEnterLeaves(state: State, data: EntryAndExitResponse.EntryAndExitListResponse) {
    state.countAll = data.countAll;
    state.enterLeaves = data.entryAndExitList;
    state.loading = false;
  },
  changeEnterLeave(state: State, enterLeave: EnterLeave) {
    const findIndex = state.enterLeaves.findIndex((value) => {
      return (value.id === enterLeave.id);
    });
    if (findIndex >= 0) {
      state.enterLeaves.splice(findIndex, 1, enterLeave);
    }
  },
  removeEnterLeave(state: State, id: string) {
    const findIndex = state.enterLeaves.findIndex((value) => {
      return (value.id === id);
    });
    if (findIndex >= 0) {
      state.enterLeaves.splice(findIndex, 1);
    }
  },
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
